/**
 * Phase 1 — question select (SPEC §2.3, DESIGN.md §3.2).
 *
 * The turn player gets the numbered board; everyone else gets a screen that says
 * whose turn it is and roughly nothing else, because the ritual is *watching
 * someone pick a number*, which happens in the room, not on the phone.
 */
import { useState } from 'react';
import { deckSizeFor } from '@idgame/protocol';
import { NumberGrid, Screen, WaitingFor, LicenseCard } from '../../components/index.js';
import {
  useIsHost,
  useIsTurnPlayer,
  useSelectQuestion,
  useSkipOffer,
  useTurnPlayer,
  useUsedQuestionNumbers,
  useHostControls,
  useSettings,
} from '../../store/index.js';
import { Button } from '../../components/index.js';

export function QuestionSelectScreen() {
  const isTurnPlayer = useIsTurnPlayer();
  const turnPlayer = useTurnPlayer();
  const used = useUsedQuestionNumbers();
  // The board is the size of *this room's* deck: safe mode's is 88, not 201.
  const deckSize = deckSizeFor(useSettings()?.deckId ?? '');
  const selectQuestion = useSelectQuestion();
  /**
   * The tap selects; the sticky action commits. `sent` is the latch that makes
   * a second commit impossible — the server owns the phase transition, so the
   * screen stays on-screen for a beat after the intent goes out, and a second
   * `select_question` for a number the room has already burned is a protocol
   * error we would be authoring ourselves.
   */
  const [selected, setSelected] = useState<number | null>(null);
  const [sent, setSent] = useState(false);

  const eyebrow = `${turnPlayer?.name ?? 'Someone'} chooses`;
  const meta = `${used.length} used`;

  if (!isTurnPlayer) {
    return (
      <Screen eyebrow={eyebrow} meta={meta} testId="question-select-screen">
        <WaitingFor headline={`${turnPlayer?.name ?? 'Someone'} is picking a number.`}>
          {turnPlayer && (
            <div className="w-40 pt-2">
              <LicenseCard player={turnPlayer} variant="card" />
            </div>
          )}
        </WaitingFor>
        <SkipOffer />
      </Screen>
    );
  }

  return (
    <Screen
      eyebrow={eyebrow}
      meta={meta}
      title="Pick a number."
      sub={`Scroll through all ${deckSize}.`}
      testId="question-select-screen"
      footer={
        selected !== null && (
          <Button
            data-testid="reveal-number"
            disabled={sent}
            onClick={() => {
              if (sent) return;
              setSent(true);
              selectQuestion(selected);
            }}
          >
            Reveal {selected}
          </Button>
        )
      }
    >
      {/*
        The board scrolls inside the screen while the heading and the sticky
        action stay put. The reserve above and below it is constant, so the
        action appearing does not move a single tile.
      */}
      <div className="flex min-h-0 flex-1 flex-col">
        <NumberGrid
          used={used}
          max={deckSize}
          selected={selected}
          disabled={sent}
          onSelect={setSelected}
        />
      </div>
    </Screen>
  );
}

/**
 * SPEC §5.4 — after the 30s grace period the host is offered "Skip [name]'s
 * turn". Rendered here and on the rank-and-predict screen, the two phases where
 * a missing turn player stalls the whole room.
 */
export function SkipOffer() {
  const isHost = useIsHost();
  const offer = useSkipOffer();
  const { skipTurn } = useHostControls();
  if (!isHost || !offer) return null;

  return (
    <div className="mt-3 shrink-0 rounded-card border border-line bg-cream p-3" data-testid="skip-offer">
      <p className="text-[0.8125rem] text-ink">
        <span className="font-semibold">{offer.name}</span> has been gone a while.
      </p>
      <Button variant="danger" className="mt-2" onClick={() => skipTurn(offer.id)} data-testid="skip-turn">
        Skip {offer.name}&rsquo;s turn
      </Button>
      <p className="pt-1.5 text-center text-[0.6875rem] text-ink-soft">
        The turn is voided — nobody scores.
      </p>
    </div>
  );
}
